import React, { useState, useEffect } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Divider,
  useTheme,
  Collapse,
} from '@mui/material';
import {
  People as PeopleIcon,
  Dashboard as DashboardIcon,
  AttachMoney as MoneyIcon,
  Assignment as AssignmentIcon,
  Casino as CasinoIcon,
  ShoppingCart as ShoppingCartIcon,
  Receipt as ReceiptIcon,
  BeachAccess as VacationIcon,
  Sick as SickIcon,
  AccountBalance as AccountBalanceIcon,
  RestaurantMenu as RestaurantMenuIcon,
  Event as EventIcon,
  Work as WorkIcon,
  ExpandLess,
  ExpandMore,
  Description as FormsIcon,
  BookOnline as ReservationIcon,
  EventAvailable as ThanksgivingIcon,
  MusicNote as PerformanceIcon,
  Storefront as VendorIcon,
  TableRestaurant as TableRestaurantIcon,
  UploadFile as UploadFileIcon,
  VolunteerActivism as DonationIcon,
  Savings as SavingsIcon,
  LocalAtm as PaidCashOutsIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';

const drawerWidth = 240;

interface NavigationProps {
  open: boolean;
  onClose: () => void;
}

const Navigation: React.FC<NavigationProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { user } = useAuth();
  const isAdmin = user?.type === 'admin';
  const [formsOpen, setFormsOpen] = useState(
    location.pathname === '/event-reservations' ||
    location.pathname === '/open-table-reservations' ||
    location.pathname === '/work-with-us' ||
    location.pathname === '/sick-leave-submissions'
  );

  const menuItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard',
    },
    {
      text: 'Employees',
      icon: <PeopleIcon />,
      path: '/employees',
    },
    {
      text: 'Tip Out Log',
      icon: <MoneyIcon />,
      path: '/tip-out-log',
    },
    {
      text: 'Tip Out Log Forms',
      icon: <AssignmentIcon />,
      path: '/tip-out-log-forms',
    },
    {
      text: 'Lottery',
      icon: <CasinoIcon />,
      path: '/lottery',
    },
    {
      text: 'Lottery Purchase',
      icon: <ShoppingCartIcon />,
      path: '/lottery-purchase',
    },
    {
      text: 'New Balance',
      icon: <AccountBalanceIcon />,
      path: '/new-balance',
    },
    {
      text: 'New Balance Forms',
      icon: <AssignmentIcon />,
      path: '/new-balance-forms',
    },
    {
      text: 'TIPS/Paid/Cash Outs',
      icon: <PaidCashOutsIcon />,
      path: '/tips-paid-cash-outs',
    },
    {
      text: 'Cash Deposits',
      icon: <SavingsIcon />,
      path: '/cash-deposits',
    },
    {
      text: 'Misc. Purchases',
      icon: <ReceiptIcon />,
      path: '/misc-purchases',
    },
    {
      text: 'Donation / Charity',
      icon: <DonationIcon />,
      path: '/donations',
    },
    {
      text: 'Vacation',
      icon: <VacationIcon />,
      path: '/vacation',
    },
    {
      text: 'Sick Leave Log',
      icon: <SickIcon />,
      path: '/sick-form',
    },
    {
      text: 'Thanksgiving Reservations',
      icon: <ThanksgivingIcon />,
      path: '/thanksgiving-reservations',
    },
    {
      text: 'Daily Specials',
      icon: <RestaurantMenuIcon />,
      path: '/daily-specials',
    },
    ...(isAdmin
      ? [
          {
            text: 'Vendor Information',
            icon: <VendorIcon />,
            path: '/vendors',
          },
          {
            text: 'OpenTable Imports',
            icon: <UploadFileIcon />,
            path: '/opentable-imports',
          },
        ]
      : []),
  ];

  const formsSubmenuItems = [
    {
      text: 'Event Reservations',
      icon: <EventIcon />,
      path: '/event-reservations',
    },
    {
      text: 'Open Table Reservations',
      icon: <TableRestaurantIcon />,
      path: '/open-table-reservations',
    },
    {
      text: 'Work With Us',
      icon: <WorkIcon />,
      path: '/work-with-us',
    },
    {
      text: 'Sick Leave',
      icon: <SickIcon />,
      path: '/sick-leave-submissions',
    },
  ];



  const handleNavigation = (path: string) => {
    navigate(path);
    // Don't close on desktop, only on mobile
  };

  const handleFormsToggle = () => {
    setFormsOpen(!formsOpen);
  };



  const isFormsPathActive = () => {
    return (
      location.pathname === '/event-reservations' ||
      location.pathname === '/open-table-reservations' ||
      location.pathname === '/work-with-us' ||
      location.pathname === '/sick-leave-submissions'
    );
  };



  // Auto-open submenus when on their respective pages
  useEffect(() => {
    if (
      location.pathname === '/event-reservations' ||
      location.pathname === '/open-table-reservations' ||
      location.pathname === '/work-with-us' ||
      location.pathname === '/sick-leave-submissions'
    ) {
      setFormsOpen(true);
    }
  }, [location.pathname]);

  return (
    <>
      {/* Desktop Navigation - Always visible, no title */}
      <Box
        sx={{
          display: { xs: 'none', md: 'block' },
          position: 'fixed',
          top: '64px',
          left: 0,
          width: drawerWidth,
          height: 'calc(100vh - 64px)',
          backgroundColor: 'background.paper',
          borderRight: 1,
          borderColor: 'divider',
          zIndex: theme.zIndex.drawer,
          overflow: 'auto',
        }}
      >
        <List sx={{ pt: 1, '& .MuiListItemText-primary': { fontWeight: 700 } }}>
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => handleNavigation(item.path)}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'primary.contrastText',
                    },
                  },
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}
          
          {/* Event Forms List */}
          <ListItem disablePadding>
            <ListItemButton
              selected={location.pathname === '/event-forms-list'}
              onClick={() => handleNavigation('/event-forms-list')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'primary.contrastText',
                  },
                },
              }}
            >
              <ListItemIcon>
                <EventIcon />
              </ListItemIcon>
              <ListItemText primary="Event Forms List" />
            </ListItemButton>
          </ListItem>

          {/* Performance Events List */}
          <ListItem disablePadding>
            <ListItemButton
              selected={location.pathname === '/performance-events-list'}
              onClick={() => handleNavigation('/performance-events-list')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'primary.contrastText',
                  },
                },
              }}
            >
              <ListItemIcon>
                <PerformanceIcon />
              </ListItemIcon>
              <ListItemText primary="Performance Events" />
            </ListItemButton>
          </ListItem>

          {/* Forms Submenu */}
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleFormsToggle}
              selected={isFormsPathActive()}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'primary.contrastText',
                  },
                },
              }}
            >
              <ListItemIcon>
                <FormsIcon />
              </ListItemIcon>
              <ListItemText primary="Website Forms" />
              {formsOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
          </ListItem>
          <Collapse in={formsOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {formsSubmenuItems.map((item) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton
                    selected={location.pathname === item.path}
                    onClick={() => handleNavigation(item.path)}
                    sx={{
                      pl: 4,
                      '&.Mui-selected': {
                        backgroundColor: 'primary.main',
                        color: 'primary.contrastText',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                        },
                        '& .MuiListItemIcon-root': {
                          color: 'primary.contrastText',
                        },
                      },
                    }}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Collapse>

          {/* Reservation Forms List */}
          <ListItem disablePadding>
            <ListItemButton
              selected={location.pathname === '/reservation-forms-list'}
              onClick={() => handleNavigation('/reservation-forms-list')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'primary.contrastText',
                  },
                },
              }}
            >
              <ListItemIcon>
                <ReservationIcon />
              </ListItemIcon>
              <ListItemText primary="Reservation Forms List" />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>

      {/* Mobile Navigation - Temporary drawer with title */}
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        sx={{
          display: { xs: 'block', md: 'none' },
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
            Restaurant Management
          </Typography>
        </Box>
        <Divider />
        <List sx={{ '& .MuiListItemText-primary': { fontWeight: 700 } }}>
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => {
                  handleNavigation(item.path);
                  onClose();
                }}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'primary.contrastText',
                    },
                  },
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}
          
          {/* Event Forms List */}
          <ListItem disablePadding>
            <ListItemButton
              selected={location.pathname === '/event-forms-list'}
              onClick={() => {
                handleNavigation('/event-forms-list');
                onClose();
              }}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'primary.contrastText',
                  },
                },
              }}
            >
              <ListItemIcon>
                <EventIcon />
              </ListItemIcon>
              <ListItemText primary="Event Forms List" />
            </ListItemButton>
          </ListItem>

          {/* Performance Events List */}
          <ListItem disablePadding>
            <ListItemButton
              selected={location.pathname === '/performance-events-list'}
              onClick={() => {
                handleNavigation('/performance-events-list');
                onClose();
              }}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'primary.contrastText',
                  },
                },
              }}
            >
              <ListItemIcon>
                <PerformanceIcon />
              </ListItemIcon>
              <ListItemText primary="Performance Events" />
            </ListItemButton>
          </ListItem>

          {/* Forms Submenu */}
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleFormsToggle}
              selected={isFormsPathActive()}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'primary.contrastText',
                  },
                },
              }}
            >
              <ListItemIcon>
                <FormsIcon />
              </ListItemIcon>
              <ListItemText primary="Website Forms" />
              {formsOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
          </ListItem>
          <Collapse in={formsOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {formsSubmenuItems.map((item) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton
                    selected={location.pathname === item.path}
                    onClick={() => {
                      handleNavigation(item.path);
                      onClose();
                    }}
                    sx={{
                      pl: 4,
                      '&.Mui-selected': {
                        backgroundColor: 'primary.main',
                        color: 'primary.contrastText',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                        },
                        '& .MuiListItemIcon-root': {
                          color: 'primary.contrastText',
                        },
                      },
                    }}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Collapse>

          {/* Reservation Forms List */}
          <ListItem disablePadding>
            <ListItemButton
              selected={location.pathname === '/reservation-forms-list'}
              onClick={() => {
                handleNavigation('/reservation-forms-list');
                onClose();
              }}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'primary.contrastText',
                  },
                },
              }}
            >
              <ListItemIcon>
                <ReservationIcon />
              </ListItemIcon>
              <ListItemText primary="Reservation Forms List" />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>
    </>
  );
};

export default Navigation;